import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n/LanguageContext";

const links = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/pricing", key: "pricing" },
  { to: "/how-it-works", key: "howItWorks" },
  { to: "/about", key: "about" },
  { to: "/faq", key: "faq" },
  { to: "/contact", key: "contact" },
];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const langToggle = (
    <div
      className="inline-flex items-center rounded-full border border-espresso/20 bg-white/60 p-0.5 text-xs font-semibold"
      role="group"
      aria-label="Language selector"
      data-testid="language-switcher"
    >
      {["en", "es"].map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={lang === l}
          data-testid={`language-switcher-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1.5 uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-espresso text-cream" : "text-espresso/70 hover:text-espresso"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(56,39,25,0.08)]" : ""
      } border-sand/40 bg-cream/85 backdrop-blur-md`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 md:h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" aria-label="Desert Coast Immigration — Home" data-testid="nav-logo-link">
          <Logo withText size={44} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-espresso text-cream"
                    : "text-espresso/80 hover:bg-sand/30 hover:text-espresso"
                }`
              }
            >
              {t.nav[l.key]}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {langToggle}
          <Link
            to="/contact"
            data-testid="nav-get-started-button"
            className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-espresso active:scale-95"
          >
            {t.nav.getStarted}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          {langToggle}
          <button
            type="button"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={open}
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-espresso/20 bg-white/70 p-2.5 text-espresso active:scale-95"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-sand/40 bg-cream/95 backdrop-blur-md"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-link-${l.key}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3.5 text-lg font-medium ${
                        isActive ? "bg-espresso text-cream" : "text-espresso hover:bg-sand/30"
                      }`
                    }
                  >
                    {t.nav[l.key]}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-get-started-button"
                className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-terracotta px-4 py-4 text-lg font-semibold text-white active:scale-95"
              >
                {t.nav.getStarted}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
